/**
 * MultiplayerAugmentationSelectionModal
 * Connects AugmentationSelectionModalThemed with multiplayer game-state-store.
 * Shows selection UI when it's the current player's turn, or a waiting overlay
 * when the current player has selected but the opponent has not.
 */

import type {
  Augmentation,
  Color,
  SelectAugmentationAction,
} from "@cloudnest/augmented-chess-engine";
import {
  getAugmentation,
  getAugmentationChoiceIds,
} from "@cloudnest/augmented-chess-engine";
import { useState } from "react";
import { AugmentationSelectionModalThemed } from "../../../chess-ui";
import { navigateTo } from "../../../router";
import { useLobby, useLobbyActions } from "../../../shared/store/lobby-store";
import { useGameState, useGameStateActions } from "../game-state-store";

function getAugmentationDetails(ids: string[]): Augmentation[] {
  return ids
    .map((id) => getAugmentation(id))
    .filter((aug): aug is Augmentation => aug !== undefined);
}

export function MultiplayerAugmentationSelectionModal(): JSX.Element | null {
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, playerColor } = useGameState();
  const gsActions = useGameStateActions();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!gameState || gameState.phase !== "AUGMENTATION_SELECTION") {
    return null;
  }

  const hasPlayerSelected =
    playerColor === "WHITE"
      ? (gameState.augmentationSelection?.whiteSelected ?? false)
      : (gameState.augmentationSelection?.blackSelected ?? false);

  const handleLeaveGame = () => {
    gsActions.reset();
    lobbyActions.leaveGame();
    navigateTo("/lobby");
  };

  // Player already selected — show waiting overlay
  if (hasPlayerSelected) {
    return (
      <div className="chess-modal-overlay">
        <div className="chess-modal">
          <div className="chess-modal-header">
            <h2>{"\uC99D\uAC15 \uC120\uD0DD"}</h2>
            <p>
              {
                "\uC0C1\uB300\uBC29\uC758 \uC120\uD0DD\uC744 \uAE30\uB2E4\uB9AC\uB294 \uC911..."
              }
            </p>
          </div>
          <div className="chess-status">
            <span className="chess-status-success">
              {"\uB0B4 \uC120\uD0DD: \uC644\uB8CC"}
            </span>
            {" | "}
            <span className="chess-status-pending">
              {"\uC0C1\uB300: \uC120\uD0DD \uC911..."}
            </span>
          </div>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              type="button"
              onClick={handleLeaveGame}
              className="cursor-pointer rounded-md border-none bg-gray-600 px-5 py-2 font-medium text-sm text-white transition-colors hover:bg-gray-700"
            >
              {"\uB098\uAC00\uAE30"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const choiceIds = getAugmentationChoiceIds(gameState, playerColor);
  const augmentationChoices = getAugmentationDetails(choiceIds);

  const handleSelectAugmentation = async (
    augmentationId: string,
    player: Color,
  ) => {
    if (!currentRoom || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const action: SelectAugmentationAction = {
        type: "SELECT_AUGMENTATION",
        player,
        augmentationId,
        timestamp: Date.now(),
      };

      const success = await gsActions.submitAction(action);

      if (!success) {
        setError(
          "\uC99D\uAC15 \uC120\uD0DD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "\uC99D\uAC15 \uC120\uD0DD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AugmentationSelectionModalThemed
        gameState={gameState}
        augmentationChoices={augmentationChoices}
        selectingPlayer={playerColor}
        onSelectAugmentation={handleSelectAugmentation}
      />
      <div className="fixed right-4 bottom-4 z-50 flex flex-col items-end gap-2">
        {error && (
          <div className="rounded-md bg-red-600 px-4 py-2 text-sm text-white shadow-lg">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={handleLeaveGame}
          className="cursor-pointer rounded-md border-none bg-gray-600 px-5 py-2 font-medium text-sm text-white shadow-lg transition-colors hover:bg-gray-700"
        >
          {"\uB098\uAC00\uAE30"}
        </button>
      </div>
    </>
  );
}
